
import Vue from "vue";
import { mapState, mapMutations, mapActions } from "vuex";
import { debounce } from "lodash";
import UpdateMultipleItems from "../components/UpdateMultipleItems";
import EditDate from "../components/EditDate";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle";
import { headersInterface } from "@/interfaces/Tables.interfaces";
import { utcToZonedTime } from "date-fns-tz";
import { formatInTimeZone } from "@/helpers";
import { constants } from "@/utils/constants";
import { getDataTables } from "@/mixins/getDataTables";
import { totalItemsByTable } from "@/api";
import { paginateTable } from "@/interfaces/Additional.interfaces";
import { formatReminderStatus } from "@/helpers/adminEnumLabels";

const TIMEZONE = "America/Toronto";

const easternFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZoneName: "short",
});

const parseAsUtc = (time: string): Date => {
  if (time.endsWith("Z") || time.includes("+")) return new Date(time);
  return new Date(time.replace(" ", "T") + "Z");
};

export default Vue.extend({
  name: "Shift",
  components: {
    UpdateMultipleItems,
    EditDate,
    TempStarsTableTitle
  },
  mixins: [getDataTables],
  data: () => {
    return {
      hygienistTypeCode: constants.hygienistType,
      dateSearch: "",
      jobIdSearch: "",
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      } as any,
      baseShiftHeaders: [
        { text: "ID", value: "id", align: "left" },
        { text: "Job ID", value: "jobId", align: "left" },
        { text: "Shift Date", value: "shiftDate", align: "left" },
        { text: "Posted Start", value: "postedStart", align: "left" },
        { text: "Posted End", value: "postedEnd", align: "left" },
        { text: "Actual Start", value: "actualStart", align: "left" },
        { text: "Actual End", value: "actualEnd", align: "left" },
        { text: "Type", value: "type", align: "left" },
        { text: "Total Hours", value: "totalHours", align: "left" },
        { text: "Unpaid Hours", value: "unpaidHours", align: "left" },
        { text: "Billable Hours", value: "billableHours", align: "left" },
        { text: "Modified On", value: "modifiedOn", align: "left" },
        { text: "Reminder Status", value: "remindStatus", align: "left" },
        { text: "Gap Unpaid", value: "gapUnpaid", align: "left" }
      ]
    };
  },
  computed: {
    ...mapState({
      tablename: "tablename",
      headers: "tableHeaders",
      content: "tableContent"
    }),
    tableHeaders(): Array<headersInterface> {
      return (this as any).baseShiftHeaders as Array<headersInterface>;
    }
  },
  methods: {
    formatReminderStatus,
    ...mapActions(["getPaginateData"]),
    ...mapMutations(["modify", "callSnackbar"]),
    findHeaderIndex(name: string): number {
      return this.headers.findIndex(item => item.value === name);
    },
    isoDateFormat(date: string): string {
      if (!date || date.startsWith("0000-00-00")) return "-";
      return formatInTimeZone(date, "MMM dd, yyyy", "UTC");
    },
    transform(time: string, isFormat: boolean) {
      if (!time || time.startsWith("0000-00-00")) {
        return isFormat ? "-" : null;
      }
      const dateTime = parseAsUtc(time);
      if (isNaN(dateTime.getTime())) {
        return isFormat ? "-" : null;
      }
      if (isFormat) {
        const parts = easternFormatter.formatToParts(dateTime);
        const get = (type: string) =>
          parts.find(p => p.type === type)?.value ?? "";
        return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")} ${get("timeZoneName")}`;
      }
      return utcToZonedTime(dateTime, TIMEZONE);
    },
    // Override mixin's getDataFromApi to handle date search and job ID search
    async getDataFromApi() {
      if ((this as any).loading) return;

      // Ensure options are initialized
      if (!(this as any).options || !(this as any).options.page) {
        (this as any).options = {
          page: 1,
          itemsPerPage: 10,
          sortBy: [],
          sortDesc: []
        };
      }

      (this as any).loading = true;
      const { sortBy = [], sortDesc = [] } = (this as any).options || {};

      if (sortBy.length) {
        (this as any).modifyMultiple([
          { property: "sortBy", value: sortBy[0] },
          { property: "sortDesc", value: sortDesc[0] }
        ]);
      }

      try {
        // Build search query and columns
        let searchQuery = "";
        let searchColumns: string[] = [];

        // Handle text search (ID searches)
        if ((this as any).tableSearch && (this as any).tableSearch.trim().length > 0) {
          searchQuery = (this as any).tableSearch.trim();
          // Pure numeric: shift id + job id only (fast; avoids Location join on j.locationId)
          if (/^\d+$/.test(searchQuery)) {
            searchColumns = ["s.id", "s.jobId"];
          } else {
            const searchCols = constants.enabledColumns.shiftTableSearch;
            if (searchCols && Array.isArray(searchCols)) {
              searchColumns = searchCols.map((col: string) => {
                if (col.includes(".")) return col;
                if (col === "locationId") return "j.locationId";
                return `s.${col}`;
              });
            }
          }
        }

        // Handle Job ID search
        if ((this as any).jobIdSearch && (this as any).jobIdSearch.trim().length > 0) {
          const jobIdValue = (this as any).jobIdSearch.trim();
          // When Job ID search is used, search only by s.jobId
          if (!searchColumns.includes("s.jobId")) {
            searchColumns.push("s.jobId");
          }
          if (searchQuery) {
            searchQuery = `${searchQuery} ${jobIdValue}`;
          } else {
            searchQuery = jobIdValue;
            // If only Job ID search is active, restrict columns to just jobId
            searchColumns = ["s.jobId"];
          }
        }

        // Handle date search - add to search columns if date is provided
        if ((this as any).dateSearch && (this as any).dateSearch.length > 0) {
          // Add shiftDate to search columns
          if (!searchColumns.includes("s.shiftDate")) {
            searchColumns.push("s.shiftDate");
          }
          // Combine date with text search or use date alone
          if (searchQuery) {
            searchQuery = `${searchQuery} ${(this as any).dateSearch}`;
          } else {
            searchQuery = (this as any).dateSearch;
          }
        }

        const columnKey = searchQuery.length
          ? "shiftTableSearch"
          : "shiftTable";

        // Always fetch total items when searching to get accurate count
        if ((this as any).totalItems === 0 || searchQuery.length > 0) {
          (this as any).totalItems = await totalItemsByTable({
            tableName: "Shift",
            ...(searchQuery.length && {
              search: searchQuery,
              searchColumns: searchColumns.length > 0 ? searchColumns : undefined,
              columnNames:
                constants.enabledColumns[columnKey] ||
                constants.enabledColumns.shiftTable
            })
          });
        }

        // Load the current page
        const requestData = {
          tableName: "Shift",
          limit: (this as any).options.itemsPerPage || 10,
          page: (this as any).options.page || 1,
          columnNames:
            constants.enabledColumns[columnKey] ||
            constants.enabledColumns.shiftTable,
          ...(searchQuery.length && {
            search: searchQuery,
            searchColumns: searchColumns.length > 0 ? searchColumns : undefined
          })
        } as paginateTable;

        await (this as any).getPaginateData(requestData);

        (this as any).loading = false;
      } catch (error) {
        (this as any).loading = false;
        console.error("Error loading shift data:", error);
      }
    }
  },
  mounted() {
    this.$root.$on("admin-shift-hours-refresh", this.getDataFromApi);
  },
  beforeDestroy() {
    this.$root.$off("admin-shift-hours-refresh", this.getDataFromApi);
  },
  watch: {
    dateSearch: debounce(function(this: any) {
      // Reset total items when searching to get fresh count
      this.totalItems = 0;
      // Reset to page 1 when searching
      if (this.options) {
        this.options.page = 1;
      }
      this.getDataFromApi();
    }, 500),
    jobIdSearch: debounce(function(this: any) {
      // Reset total items when searching to get fresh count
      this.totalItems = 0;
      // Reset to page 1 when searching
      if (this.options) {
        this.options.page = 1;
      }
      this.getDataFromApi();
    }, 500)
  }
});
